import React, { JSX } from "react";
import { PlayerAction, PlayerDetails, Story } from "../../../types";
import { saveFile, saveTextFile } from "../../../utils/fileSaver";
import { format } from "date-fns";
import { now } from "../../../time";
import { storyToText } from "./story-results";
import GamePage from "../game-page/game-page";
import CanvasView from "../../common/canvas-view/canvas-view";
import { makeNice, SafeJoin } from "../../../util";
import PrevSubmission from "../../common/prev-submission/prev-submission";
import Button from "@/components/ui/button";

type Props = {
  story: Story;
  me?: PlayerDetails;
  onBack?: () => void;
  onShare?: (
    story: number,
    title: string,
    char1: string,
    char2: string
  ) => void;
  onReact?: (playerAction: PlayerAction) => void;
  shareText?: string;
  loading?: boolean;
  subheaders?: string[];
  footerJsx?: JSX.Element;
};

export function cleanStoryName(story: Story) {
  return story.meta.title.replace(/[/\\?%*:|"<>]/g, "_");
}

export function fileNameTxt(story: Story) {
  // return filename.replace(/[/\\?%*:|"<>]/g, "_")

  return `FranticFanfic_${cleanStoryName(story)}_${format(now(), "LLL_dd_yyyy")}.txt`;
}

function fileNameImage(story: Story) {
  return `FranticFanfic_${cleanStoryName(story)}_${format(now(), "LLL_dd_yyyy")}.png`;
}

function handleBluesky(story: Story) {
  // Create a shareable text for the story
  const storyTitle = story.meta.title;
  const characters =
    story.characters
      ?.filter((c) => c.selected)
      .map((c) => c.name)
      .join(" & ") || "amazing characters";

  const shareText = [
    `Just created an amazing fanfic story: "${storyTitle}" featuring ${characters}!`,
    "",
    "Play Frantic Fanfic with your friends at franticfanfic.com",
  ].join("\n");

  const encodedText = encodeURIComponent(shareText);

  // Create the Bluesky intent URL
  const blueskyUrl = `https://bsky.app/intent/compose?text=${encodedText}`;

  // Open in new tab
  window.open(blueskyUrl, "_blank", "noopener,noreferrer");
}

const StoryView = ({
  story,
  onBack,
  shareText,
  me,
  subheaders,
  footerJsx,
}: Props) => {
  function titlesJsx() {
    return [
      <span className={"underline font-bold break-words"}>
        {story.meta.title}
      </span>,
      <>
        characters:{" "}
        {SafeJoin(
          story.characters
            ?.filter((c) => c.selected)
            .map<React.ReactNode>((c) => (
              <span className={"italic break-words"} key={"character-" + c.id}>
                {c.name}
              </span>
            )),
          ", "
        )}{" "}
      </>,
      <>
        writers:{" "}
        {makeNice(
          story.submissions.map<React.ReactNode>((s) => (
            <i key={"player-" + s.id}>{s.player.name}</i>
          ))
        )}{" "}
      </>,
      "Rated " + (story.settings.mature ? "18+" : "ALL AGES"),
    ];
  }

  async function handleTextClick() {
    let text = storyToText(story);
    await saveTextFile(text, fileNameTxt(story));
  }

  function coolFooter2() {
    let selected =
      story.characters?.filter((c) => c.selected).map((c) => c.name) ?? [];

    return (
      <div className={"ml-auto flex flex-row justify-end items-stretch"}>
        <Button
          className={"text-2xl mr-1"}
          disabled={false}
          onClick={() => takeScreenshot(story)}
        >
          {shareText ? shareText : "💾img"}
        </Button>

        <Button
          className={"text-2xl mr-1"}
          disabled={false}
          onClick={handleTextClick}
        >
          {shareText ? shareText : "💾txt"}
        </Button>

        {/* <Button
          className={"text-2xl mr-1"}
          disabled={false}
          onClick={() => handleBluesky(story)}
        >
          {shareText ? (
            shareText
          ) : (
            <span>
              <BlueskyIcon className="inline-block" />
              BSKY
            </span>
          )}
        </Button> */}
      </div>
    );
  }

  async function takeScreenshot(story: Story) {
    const { default: html2canvas } = await import("html2canvas");
    const canvasId = `canvas-${story.id}`;
    const canvasEl = document.querySelector(`#${canvasId}`) as HTMLElement;
    if (!canvasEl) return;
    const canvas = await html2canvas(canvasEl, { scrollY: -window.scrollY });

    canvas.toBlob(async (blob) => {
      if (blob) {
        await saveFile(blob, fileNameImage(story));
      }
    }, "image/png");
  }

  return (
    <GamePage
      hack={[<CanvasView key={"canvas-" + story.id} story={story} />]}
      titlesJsx={titlesJsx()}
      onBack={onBack}
      footerJsx={footerJsx ? footerJsx : coolFooter2()}
      subHeaders={subheaders}
    >
      <div className="w-full flex-grow flex flex-col divide-y divide-gray-200">
        {story.submissions.map<React.ReactNode>((s, idx) => {
          return (
            <div className="p-4 text-lg break-words whitespace-pre-wrap normal-case" key={"story-" + s.id}>
              <PrevSubmission submission={s} variant="read" />
            </div>
          );
        })}
      </div>
    </GamePage>
  );

  // <Page subHeaders={subheaders} styled-header={styled-header} onBack={onBack}
  //              footerJsx={footerJsx ? footerJsx : coolFooter2()}
  //              hack={[<CanvasView key={"story-" + story.id} story={story}/>]}>
  //
  // </Page>
};

export default StoryView;
